import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ItemIcon from '../../../components/ItemIcon';
import { WikiSearchResponseDto, WikiSearchResultDto } from '../../../types';
import { TEXT_COLORS, BG_COLORS, BORDER_COLORS, ACCENT_COLORS } from '../../../utils/colors';

export interface WikiSearchSelected {
  source: string;
  id: number;
}

interface WikiSearchResultsProps {
  results: WikiSearchResponseDto;
  /** Открыть карточку в боковой панели (десктоп). Если не задан — клик уходит на страницу. */
  onPreview?: (source: string, id: number) => void;
  /** Текущий выбранный результат (для подсветки), когда открыто превью. */
  selected?: WikiSearchSelected | null;
  /** Открыта боковая панель — сузить сетку результатов. */
  isPreviewOpen?: boolean;
}

interface ResultRowProps {
  item: WikiSearchResultDto;
  onPreview?: (source: string, id: number) => void;
  isSelected: boolean;
}

const ResultRow: React.FC<ResultRowProps> = ({ item, onPreview, isSelected }) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (onPreview) onPreview(item.source, item.id);
    else navigate(`/wiki/${item.source}/details/${item.id}`);
  };
  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        borderRadius: 2,
        cursor: 'pointer',
        // Строка — элемент grid-сетки, у которого min-width по умолчанию = auto, то есть не меньше
        // min-content. Из-за nowrap-имени внутри трек `1fr` растягивался по самому длинному названию
        // («Рецепт: Эльфийский Меч (60%)» просил 298px в колонке 265px), и карточка вылезала за
        // подложку с обрезанным текстом — ellipsis внутри при этом не срабатывал.
        minWidth: 0,
        border: `1px solid ${isSelected ? ACCENT_COLORS.PRIMARY : BORDER_COLORS.DEFAULT}`,
        background: isSelected ? BG_COLORS.GLASS : BG_COLORS.GLASS_DARK,
        transition: 'background 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          background: BG_COLORS.GLASS,
          borderColor: isSelected ? ACCENT_COLORS.PRIMARY : ACCENT_COLORS.BORDER,
        },
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <ItemIcon icon={item.icon || ''} name={item.name} size={40} />
      </Box>
      {/* Имя + доп. название стоят ВПЛОТНУЮ друг к другу: `flex: 1` держит эта обёртка, а не имя,
          иначе имя растягивалось бы на всю строку и суффикс уезжал к правому краю, далеко от названия. */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            minWidth: 0,
            color: TEXT_COLORS.LIGHT,
            fontSize: '0.9rem',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.name}
        </Typography>
        {/* Доп. название из клиента («[MA]») — только у предметов, у скиллов и NPC его нет.
            Отдельным элементом, а не внутри имени: суффикс короткий и не должен съедаться ellipsis'ом. */}
        {item.additionalName && (
          <Typography
            sx={{
              flexShrink: 0,
              color: TEXT_COLORS.SECONDARY,
              fontSize: '0.85rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {item.additionalName}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface GroupProps {
  title: string;
  items: WikiSearchResultDto[];
  onPreview?: (source: string, id: number) => void;
  selected?: WikiSearchSelected | null;
  isPreviewOpen?: boolean;
}

const Group: React.FC<GroupProps> = ({ title, items, onPreview, selected, isPreviewOpen }) => {
  if (!items.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          mb: 1,
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: TEXT_COLORS.MUTED,
        }}
      >
        {title} · {items.length}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isPreviewOpen
            ? { xs: '1fr', xl: '1fr 1fr' }
            : { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
          gap: 1,
        }}
      >
        {items.map((it) => (
          <ResultRow
            key={`${it.source}-${it.id}`}
            item={it}
            onPreview={onPreview}
            isSelected={!!selected && selected.source === it.source && selected.id === it.id}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * Результаты глобального поиска по вики, сгруппированные (предметы / умения / NPC).
 * Клик открывает карточку в боковой панели (как в вики) через onPreview; без него — переход на страницу.
 */
const WikiSearchResults: React.FC<WikiSearchResultsProps> = ({ results, onPreview, selected, isPreviewOpen }) => {
  const { t } = useTranslation('wiki');
  const total = results.items.length + results.skills.length + results.npcs.length;

  if (total === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: TEXT_COLORS.TERTIARY }}>
        <Typography sx={{ fontSize: '0.95rem' }}>{t('search.empty')}</Typography>
      </Box>
    );
  }

  const groupProps = { onPreview, selected, isPreviewOpen };

  return (
    <Box>
      <Group title={t('search.groups.items')} items={results.items} {...groupProps} />
      <Group title={t('search.groups.skills')} items={results.skills} {...groupProps} />
      <Group title={t('search.groups.npcs')} items={results.npcs} {...groupProps} />
    </Box>
  );
};

export default WikiSearchResults;
